/**
 * MDS file format migrations
 *
 * This module handles migration of MDS files from older versions to the current version.
 * Each migration function transforms data from one version to the next.
 *
 * See docs/mds-migration.md for detailed specification.
 */

import type { MIDASProject } from '../../types';
import packageInfo from '../../../package.json';
import logger from '../utils/logger';
import { REPORT_ELEMENT_SIZING } from '../constants/reportElementSizing';

/**
 * Migration function type
 * - Input: unknown (old data may not conform to current MIDASProject type)
 * - Output: MIDASProject (fully compliant with current type)
 */
export type Migration = (data: unknown) => MIDASProject;

/**
 * Registry of all migration functions
 * Key: version string (YYYY.MM.DD format)
 * Value: migration function that upgrades TO that version
 */
export const migrations: Record<string, Migration> = {
  '2025.10.28': migrate_2025_10_28,
  '2025.11.24-1': migrate_2025_11_24,
  '2025.12.03': migrate_2025_12_03,
};

/**
 * Apply all necessary migrations to bring data to current version
 * @param data - Raw data loaded from file (may be any old version)
 * @returns Migrated data conforming to current MIDASProject type
 */
export function migrateToLatest(data: unknown): MIDASProject {
  // Extract version from data, default to '0000.00.00' if not present (YYYY.MM.DD format)
  const fileVersion =
    (typeof data === 'object' && data !== null && 'version' in data && typeof data.version === 'string')
      ? data.version
      : '0000.00.00';
  const currentVersion = packageInfo.version;

  logger.info('[migrations] Starting migration', {
    fileVersion,
    currentVersion,
    availableMigrations: Object.keys(migrations).length
  });

  // Get all migration versions in sorted order
  const migrationVersions = Object.keys(migrations).sort();

  // Filter migrations that need to be applied (file version < migration version <= current version)
  const applicableMigrations = migrationVersions.filter(
    version => fileVersion < version && version <= currentVersion
  );

  if (applicableMigrations.length === 0) {
    logger.info('[migrations] No migrations needed', {
      fileVersion,
      currentVersion
    });
    return data as MIDASProject;
  }

  logger.info('[migrations] Applying migrations', {
    count: applicableMigrations.length,
    versions: applicableMigrations
  });

  // Apply migrations sequentially
  let migrated: unknown = data;
  for (const version of applicableMigrations) {
    logger.info('[migrations] Applying migration', { version });
    try {
      migrated = migrations[version](migrated);
    } catch (error) {
      logger.error('[migrations] Migration failed', {
        version,
        error: error instanceof Error ? error.message : String(error)
      });
      throw new Error(`Migration to version ${version} failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  const finalVersion =
    (typeof migrated === 'object' && migrated !== null && 'version' in migrated && typeof migrated.version === 'string')
      ? migrated.version
      : 'unknown';

  logger.info('[migrations] Migrations completed successfully', {
    finalVersion
  });

  return migrated as MIDASProject;
}

// ===== Individual Migration Functions =====

/**
 * Migration to version 2025.10.28
 * - Add width and height fields to ReportElement
 * - Defaults are defined in REPORT_ELEMENT_SIZING constant
 *
 * Related: Issue #152
 */
function migrate_2025_10_28(data: unknown): MIDASProject {
  const oldData = data as Record<string, unknown>;

  logger.info('[migrations] Applying 2025.10.28 migration: Adding width/height to ReportElements');

  // 古い形式のreports（elements配列）を処理
  interface OldElement { width?: number; height?: number; [key: string]: unknown }
  interface OldReport { elements?: OldElement[]; [key: string]: unknown }

  const oldReports = oldData.reports as OldReport[] | undefined;

  return {
    ...oldData,
    version: '2025.10.28',
    reports: oldReports?.map(report => ({
      ...report,
      elements: Array.isArray(report.elements) ? report.elements.map(element => ({
        ...element,
        width: element.width ?? REPORT_ELEMENT_SIZING.DEFAULT_WIDTH,
        height: element.height ?? REPORT_ELEMENT_SIZING.DEFAULT_HEIGHT
      })) : report.elements
    })) || []
  } as unknown as MIDASProject;
}

/**
 * Migration to version 2025.11.24-1
 * - Rename operation type from 'mql_query' to 'sql_query' in DerivedDataSet
 *
 * Related: Issue #219
 */
function migrate_2025_11_24(data: unknown): MIDASProject {
  const oldData = data as Partial<MIDASProject>;

  logger.info('[migrations] Applying 2025.11.24-1 migration: Renaming mql_query to sql_query');

  // datasets内のDerivedDataSetのoperation.typeを変換
  const migratedDatasets: Record<string, unknown> = {};
  if (oldData.datasets) {
    for (const [key, dataset] of Object.entries(oldData.datasets)) {
      const ds = dataset as unknown as Record<string, unknown>;
      if (ds.operation && typeof ds.operation === 'object') {
        const operation = ds.operation as Record<string, unknown>;
        if (operation.type === 'mql_query') {
          migratedDatasets[key] = {
            ...ds,
            operation: {
              ...operation,
              type: 'sql_query'
            }
          };
          continue;
        }
      }
      migratedDatasets[key] = dataset;
    }
  }

  return {
    ...oldData,
    version: '2025.11.24-1',
    datasets: migratedDatasets
  } as MIDASProject;
}

/**
 * Migration to version 2025.12.03
 * - Convert Report.elements from array to dictionary
 * - Add Report.content field with element references
 * - Convert TextMemoElement to Markdown text
 *
 * Related: Issue #287
 */
function migrate_2025_12_03(data: unknown): MIDASProject {
  const oldData = data as Record<string, unknown>;

  logger.info('[migrations] Applying 2025.12.03 migration: Converting Report to Markdown format');

  interface OldReportElement {
    id: string;
    type: string;
    content?: string;
    [key: string]: unknown;
  }

  interface OldReport {
    id: string;
    name: string;
    description?: string;
    elements?: OldReportElement[] | Record<string, OldReportElement>;
    content?: string;
    reportSettings?: unknown;
    metadata?: unknown;
  }

  const oldReports = oldData.reports as OldReport[] | undefined;

  const migratedReports = oldReports?.map((report: OldReport) => {
    // 旧形式（配列）かどうかチェック
    if (!Array.isArray(report.elements)) {
      // 既に新形式の場合はそのまま返す
      return report;
    }

    const elementsDict: Record<string, OldReportElement> = {};
    const contentParts: string[] = [];

    for (const element of report.elements) {
      if (element.type === 'text_memo') {
        // TextMemoElementはMarkdownテキストに変換
        const textContent = element.content || '';
        contentParts.push(textContent);
      } else {
        // その他の要素は辞書に追加し、参照を挿入
        elementsDict[element.id] = element;
        contentParts.push(`{{${element.type}:${element.id}}}`);
      }
    }

    return {
      ...report,
      content: contentParts.join('\n\n'),
      elements: elementsDict
    };
  }) || [];

  return {
    ...oldData,
    version: '2025.12.03',
    reports: migratedReports
  } as unknown as MIDASProject;
}
